import { ModelControlEnum } from '@qse/constants'
import { useActiveModelStore } from '@qse/stores'

import { detectCurrentApp, type PortalFlavor } from './app-detection'

type AccessAppSurface = 'dashboard' | 'admin' | 'super-admin' | 'tenant-admin'

interface AccessDeniedConfig {
  userRole: string
  currentApp: AccessAppSurface
  targetApp: AccessAppSurface
  reason?: 'access_denied' | 'incomplete_registration'
}

const portalHostUrls = (): Record<AccessAppSurface, { dev: string; prod: string }> => ({
  'super-admin': {
    dev: import.meta.env.VITE_SUPER_ADMIN_APP_URL || 'http://localhost:5175/',
    prod: import.meta.env.VITE_SUPER_ADMIN_APP_URL || 'https://super-admin.dev.scopetech.ai/',
  },
  'tenant-admin': {
    dev: import.meta.env.VITE_TENANT_ADMIN_APP_URL || 'http://localhost:5174/',
    prod: import.meta.env.VITE_TENANT_ADMIN_APP_URL || 'https://tenant-admin.dev.scopetech.ai/',
  },
  admin: {
    dev: import.meta.env.VITE_ADMIN_APP_URL || 'http://localhost:5174/',
    prod: import.meta.env.VITE_ADMIN_APP_URL || 'https://admin.dev.scopetech.ai/',
  },
  dashboard: {
    dev: import.meta.env.VITE_DASHBOARD_APP_URL || 'http://localhost:5173/',
    prod: import.meta.env.VITE_DASHBOARD_APP_URL || 'https://app.dev.scopetech.ai/',
  },
})

/**
 * Show access denied popup and handle redirect based on user role and current app
 */
export const showAccessDeniedPopup = (config: AccessDeniedConfig) => {
  const { userRole, currentApp, targetApp } = config

  const getRedirectUrl = () => {
    const isDevelopment = import.meta.env.DEV || window.location.hostname === 'localhost'
    const hosts = portalHostUrls()
    const entry = hosts[targetApp] || hosts.dashboard
    return isDevelopment ? entry.dev : entry.prod
  }

  const redirectUrl = getRedirectUrl()

  useActiveModelStore.setState({
    data: {
      userRole,
      currentApp,
      targetApp,
      redirectUrl,
      reason: config.reason || 'access_denied',
      modalHeader: {
        title: 'Access Denied',
        showCloseButton: false,
      },
      onBeforeClose: () => false,
    },
    width: 'max-w-[500px]',
    modelActive: ModelControlEnum.ACCESS_DENIED,
    toggleModel: true,
    scrollBehavior: 'inside',
  })
}

const portalToSurface = (portal: PortalFlavor): AccessAppSurface => {
  if (portal === 'super-admin' || portal === 'tenant-admin' || portal === 'dashboard') {
    return portal
  }
  return 'admin'
}

/**
 * Check if user has access to current portal image and handle access denied scenarios
 */
export const checkAppAccess = (userRole: string): boolean => {
  const detected = detectCurrentApp()
  const currentApp = portalToSurface(detected.portal)

  // Hardened allowlists for separate portal images
  const accessRules: Record<
    string,
    { allowedApps: AccessAppSurface[]; defaultApp: AccessAppSurface }
  > = {
    SUPER_ADMIN: {
      allowedApps: ['super-admin', 'admin'],
      defaultApp: 'super-admin',
    },
    TENANT_ADMIN: {
      allowedApps: ['tenant-admin', 'admin', 'dashboard'],
      defaultApp: 'tenant-admin',
    },
    TENANT_USER: {
      allowedApps: ['dashboard'],
      defaultApp: 'dashboard',
    },
    RESELLER_ADMIN: {
      allowedApps: ['admin', 'super-admin'],
      defaultApp: 'super-admin',
    },
  }

  const userRule = accessRules[userRole]

  if (!userRule) {
    console.error(`Unknown user role: ${userRole}`)
    return false
  }

  const hasAccess = userRule.allowedApps.includes(currentApp)

  if (!hasAccess) {
    showAccessDeniedPopup({
      userRole,
      currentApp,
      targetApp: userRule.defaultApp,
    })
    return false
  }

  return true
}
